(function () {
  'use strict';

  /* =========================================================
     SHAPESHIFTER theme JS.
     Event delegation on document keeps everything working after
     the Shopify theme editor re-renders a section.
     ========================================================= */

  var body = document.body;
  var docEl = document.documentElement;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var animEnabled = body.getAttribute('data-anim') !== 'false' && !reducedMotion;
  var particlesEnabled = body.getAttribute('data-particles') !== 'false' && !reducedMotion && window.innerWidth > 900;
  var intensity = body.getAttribute('data-anim-intensity') || 'full';
  if (!animEnabled || intensity === 'reduced') docEl.classList.add('no-anim');
  docEl.classList.add('js');

  /* ---------- Money formatting ---------- */
  var currency = body.getAttribute('data-currency') || 'USD';
  var money = (function () {
    try {
      var fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency });
      return function (cents) { return fmt.format(cents / 100); };
    } catch (err) {
      return function (cents) { return '$' + (cents / 100).toFixed(2); };
    }
  })();

  /* =========================================================
     HEADER — scroll state + rotating announcements
     ========================================================= */
  var headerEl = null;
  function getHeader() {
    if (!headerEl || !headerEl.isConnected) headerEl = document.querySelector('.ss-header');
    return headerEl;
  }
  function onScrollHeader() {
    var header = getHeader();
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 24);
  }
  window.addEventListener('scroll', onScrollHeader, { passive: true });
  onScrollHeader();

  /* Measure header height → --header-h, so the homepage hero can pull
     itself up underneath the transparent header regardless of logo size. */
  function measureHeader() {
    var header = document.querySelector('.ss-header');
    if (!header) return;
    docEl.style.setProperty('--header-h', header.offsetHeight + 'px');
  }
  measureHeader();
  window.addEventListener('resize', measureHeader);
  window.addEventListener('load', measureHeader);

  function initAnnouncements() {
    var bar = document.querySelector('.ss-announce');
    if (!bar) return;
    var items = bar.querySelectorAll('.ss-announce__item');
    if (items.length < 2) return;
    if (bar._timer) clearInterval(bar._timer);
    var i = 0;
    bar._timer = setInterval(function () {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, parseInt(bar.getAttribute('data-interval') || '5000', 10));
  }
  initAnnouncements();

  /* ---------- Mobile nav ---------- */
  document.addEventListener('click', function (e) {
    var burger = e.target.closest('.ss-burger');
    if (!burger) return;
    var nav = burger.closest('.ss-header').querySelector('.ss-nav');
    if (!nav) return;
    var open = nav.classList.toggle('is-open');
    burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    body.style.overflow = open ? 'hidden' : '';
  });
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('.ss-subtoggle');
    if (!toggle) return;
    e.preventDefault();
    var li = toggle.closest('li');
    var open = li.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* =========================================================
     REVEAL SYSTEM (IntersectionObserver)
     ========================================================= */
  var revealObserver = null;
  function observeReveals(root) {
    if (!('IntersectionObserver' in window)) {
      (root || document).querySelectorAll('[data-reveal],[data-reveal-stagger]').forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    (root || document).querySelectorAll('[data-reveal],[data-reveal-stagger]').forEach(function (el) {
      revealObserver.observe(el);
    });
  }
  observeReveals();

  /* Once a reveal animation finishes, detach it so its filled end-state
     can't override hover transforms (which would make cards feel laggy). */
  document.addEventListener('animationend', function (e) {
    if (e.animationName && e.animationName.indexOf('ss-rev') === 0) {
      e.target.classList.add('ss-anim-done');
    }
  });

  /* =========================================================
     HERO — choreographed entrance
     ========================================================= */
  function initHero(root) {
    (root || document).querySelectorAll('.ss-hero').forEach(function (hero) {
      requestAnimationFrame(function () { hero.classList.add('is-ready'); });
    });
  }
  initHero();

  /* =========================================================
     IN-VIEW TRACKING — pauses continuous animations off-screen
     and drives the guarantee seal's scroll tilt.
     ========================================================= */
  var inviewObserver = null;
  function initInView(root) {
    if (!('IntersectionObserver' in window)) return;
    if (!inviewObserver) {
      inviewObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.classList.toggle('is-inview', entry.isIntersecting);
          /* Seal sections get an immediate tilt update on entry so the
             rotation is correct even before the next scroll tick. */
          if (entry.isIntersecting && entry.target.classList.contains('ss-guar')) updateSeals();
        });
      }, { rootMargin: '60px 0px' });
    }
    (root || document).querySelectorAll('.ss-hero, .ss-marquee, .ss-guar').forEach(function (el) {
      inviewObserver.observe(el);
    });
  }
  initInView();

  /* Guarantee seal — a few degrees of rotation tied to scroll position */
  var sealTicking = false;
  function updateSeals() {
    sealTicking = false;
    if (docEl.classList.contains('no-anim')) return;
    document.querySelectorAll('.ss-guar.is-inview').forEach(function (section) {
      var r = section.getBoundingClientRect();
      var progress = (window.innerHeight / 2 - (r.top + r.height / 2)) / window.innerHeight;
      var rot = Math.max(-14, Math.min(14, progress * 26));
      section.style.setProperty('--seal-rot', rot.toFixed(2) + 'deg');
    });
  }
  window.addEventListener('scroll', function () {
    if (!sealTicking) { sealTicking = true; requestAnimationFrame(updateSeals); }
  }, { passive: true });
  updateSeals();

  /* =========================================================
     SHAPESHIFT SCROLL SEQUENCE
     ========================================================= */
  function initShift() {
    var shifts = document.querySelectorAll('.ss-shift');
    if (!shifts.length || docEl.classList.contains('no-anim')) return;
    shifts.forEach(function (section) {
      var words = section.querySelectorAll('.ss-shift__word');
      var product = section.querySelector('.ss-shift__product');
      if (!words.length) return;
      var steps = words.length;
      section.style.setProperty('--steps', steps);
      var ticking = false;
      function update() {
        ticking = false;
        var rect = section.getBoundingClientRect();
        var total = rect.height - window.innerHeight;
        if (total <= 0) return;
        var progress = Math.min(1, Math.max(0, -rect.top / total));
        section.classList.toggle('is-active', progress > 0.01 && progress < 0.99);
        var idx = Math.min(steps - 1, Math.floor(progress * steps));
        words.forEach(function (w, i) {
          w.classList.toggle('is-on', i === idx);
          w.classList.toggle('is-past', i < idx);
        });
        var word = words[idx];
        var c = word && word.getAttribute('data-color');
        if (c) section.style.setProperty('--flavor', c);
        if (product) {
          product.style.transform = 'translateX(-50%) rotate(' + ((progress - 0.5) * 14) + 'deg)';
        }
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      update();
    });
  }
  initShift();

  /* =========================================================
     PARTICLES — pure-CSS drift, composited on the GPU.
     No canvas, no rAF loop: zero main-thread work while
     scrolling (the old canvas painting was the #1 jank source).
     ========================================================= */
  function initParticles() {
    if (!particlesEnabled || docEl.classList.contains('no-anim')) return;
    document.querySelectorAll('[data-particles]').forEach(function (host) {
      if (host._particles) return;
      host._particles = true;
      var wrap = document.createElement('div');
      wrap.className = 'ss-particles';
      wrap.setAttribute('aria-hidden', 'true');
      var count = Math.min(14, Math.max(6, Math.floor(host.offsetWidth / 110)));
      for (var i = 0; i < count; i++) {
        var s = document.createElement('span');
        s.className = 'ss-particle';
        var size = (Math.random() * 2.4 + 1.2).toFixed(1);
        var dur = (Math.random() * 22 + 18).toFixed(1);
        s.style.cssText =
          'left:' + (Math.random() * 100).toFixed(1) + '%;' +
          'width:' + size + 'px;height:' + size + 'px;' +
          'opacity:' + (Math.random() * 0.4 + 0.08).toFixed(2) + ';' +
          '--px:' + Math.round(Math.random() * 90 - 45) + 'px;' +
          'animation-duration:' + dur + 's;' +
          'animation-delay:-' + (Math.random() * dur).toFixed(1) + 's;';
        wrap.appendChild(s);
      }
      host.appendChild(wrap);
      if (inviewObserver) inviewObserver.observe(host);
    });
  }
  initParticles();

  /* =========================================================
     PRODUCT UNIVERSE — hover shapeshift
     ========================================================= */
  document.addEventListener('mouseover', function (e) {
    var card = e.target.closest('[data-flavor-color]');
    if (!card) return;
    var scope = card.closest('[data-flavor-scope]');
    if (!scope) return;
    scope.style.setProperty('--flavor', card.getAttribute('data-flavor-color'));
  });

  /* =========================================================
     PDP — flavor swatches, pills, qty, sticky ATC
     ========================================================= */
  function pdpRoot(el) { return el.closest('[data-product-root]'); }

  function setVariant(root, variantId) {
    var data = root._variants;
    if (!data) {
      try { data = JSON.parse(root.querySelector('[data-variants-json]').textContent); root._variants = data; }
      catch (err) { return; }
    }
    var v = data.find(function (x) { return String(x.id) === String(variantId); });
    if (!v) return;
    var idInput = root.querySelector('input[name="id"], select[name="id"]');
    if (idInput) idInput.value = v.id;
    root.querySelectorAll('[data-price]').forEach(function (el) { el.textContent = money(v.price); });
    root.querySelectorAll('[data-compare-price]').forEach(function (el) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        el.textContent = money(v.compare_at_price); el.style.display = '';
      } else { el.style.display = 'none'; }
    });
    var atc = root.querySelector('[data-atc]');
    if (atc) {
      atc.disabled = !v.available;
      var label = atc.querySelector('[data-atc-label]') || atc;
      label.textContent = v.available ? (atc.getAttribute('data-label-add') || 'Add to Cart') : (atc.getAttribute('data-label-oos') || 'Sold Out');
    }
    root.querySelectorAll('[data-stock]').forEach(function (el) {
      el.textContent = v.available ? el.getAttribute('data-in') : el.getAttribute('data-out');
      el.classList.toggle('ss-pdp__stock--out', !v.available);
    });
    if (v.featured_image && v.featured_image.src) {
      var img = root.querySelector('[data-main-image]');
      if (img) {
        img.classList.add('is-fading');
        setTimeout(function () { img.src = v.featured_image.src; img.classList.remove('is-fading'); }, 220);
      }
    }
    if (history.replaceState && root.getAttribute('data-url')) {
      history.replaceState(null, '', root.getAttribute('data-url') + '?variant=' + v.id);
    }
    root.dispatchEvent(new CustomEvent('ss:variant', { detail: v, bubbles: true }));
  }

  function currentOptions(root) {
    var opts = [];
    root.querySelectorAll('[data-option-index]').forEach(function (group) {
      var active = group.querySelector('.is-active');
      if (active) opts[parseInt(group.getAttribute('data-option-index'), 10)] = active.getAttribute('data-value');
    });
    return opts;
  }

  function matchVariant(root) {
    var data = root._variants || JSON.parse(root.querySelector('[data-variants-json]').textContent);
    root._variants = data;
    var opts = currentOptions(root);
    var v = data.find(function (x) {
      return opts.every(function (val, i) { return val === undefined || x.options[i] === val; });
    });
    if (v) setVariant(root, v.id);
  }

  document.addEventListener('click', function (e) {
    var swatch = e.target.closest('.ss-flavor-swatch, .ss-pill');
    if (!swatch) return;
    var group = swatch.closest('[data-option-index]');
    var root = pdpRoot(swatch);
    if (!group || !root) return;
    e.preventDefault();
    group.querySelectorAll('.is-active').forEach(function (el) { el.classList.remove('is-active'); });
    swatch.classList.add('is-active');
    var color = swatch.getAttribute('data-swatch-color');
    if (color) {
      root.style.setProperty('--flavor', color);
      var name = root.querySelector('[data-flavor-name]');
      if (name) name.textContent = swatch.getAttribute('data-value');
    }
    matchVariant(root);
  });

  document.addEventListener('change', function (e) {
    var sel = e.target.closest('select[data-variant-select]');
    if (!sel) return;
    var root = pdpRoot(sel);
    if (root) setVariant(root, sel.value);
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    var input = btn.parentElement.querySelector('input');
    if (!input) return;
    var val = parseInt(input.value || '1', 10) + parseInt(btn.getAttribute('data-qty-change'), 10);
    input.value = Math.max(1, val);
  });

  /* PDP gallery thumbs */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.ss-pdp__thumb');
    if (!btn) return;
    var root = pdpRoot(btn) || document;
    var mainImg = root.querySelector('[data-main-image]');
    if (mainImg) {
      mainImg.classList.add('is-fading');
      setTimeout(function () { mainImg.src = btn.getAttribute('data-full'); mainImg.classList.remove('is-fading'); }, 200);
    }
    root.querySelectorAll('.ss-pdp__thumb').forEach(function (b) { b.classList.remove('is-active'); });
    btn.classList.add('is-active');
  });

  /* Sticky mobile ATC — show once the buy box scrolls out of view */
  function initStickyAtc() {
    var sticky = document.querySelector('.ss-sticky-atc');
    var buyBox = document.querySelector('[data-buy-box]');
    if (!sticky || !buyBox || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0 }).observe(buyBox);
  }
  initStickyAtc();
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sticky-atc-trigger]');
    if (!btn) return;
    var atc = document.querySelector('[data-buy-box] [data-atc]');
    if (atc) atc.click();
  });

  /* =========================================================
     PRODUCT FINDER
     ========================================================= */
  document.addEventListener('click', function (e) {
    var opt = e.target.closest('.ss-finder__opt');
    if (!opt) return;
    var finder = opt.closest('.ss-finder');
    finder.querySelectorAll('.ss-finder__opt').forEach(function (o) { o.classList.remove('is-selected'); });
    opt.classList.add('is-selected');
    var result = finder.querySelector('.ss-finder__result');
    if (!result) return;
    result.querySelector('[data-finder-title]').textContent = opt.getAttribute('data-title') || '';
    result.querySelector('[data-finder-desc]').textContent = opt.getAttribute('data-desc') || '';
    var link = result.querySelector('[data-finder-link]');
    if (link) link.href = opt.getAttribute('data-url') || '#';
    var img = result.querySelector('[data-finder-img]');
    if (img) {
      var src = opt.getAttribute('data-img');
      img.style.display = src ? '' : 'none';
      if (src) img.src = src;
    }
    var accent = opt.getAttribute('data-accent');
    if (accent) finder.style.setProperty('--flavor', accent);
    result.classList.add('is-visible');
  });

  /* =========================================================
     CART — AJAX add + drawer
     ========================================================= */
  var drawer = document.querySelector('.ss-drawer');
  var drawerEnabled = drawer && drawer.getAttribute('data-enabled') === 'true';

  function openDrawer(justAdded) {
    if (!drawer) return;
    body.classList.add('drawer-open');
    var locked = drawer.querySelector('.ss-drawer__locked');
    if (locked) locked.classList.toggle('is-visible', !!justAdded);
    drawer.setAttribute('aria-hidden', 'false');
  }
  function closeDrawer() {
    body.classList.remove('drawer-open');
    if (drawer) drawer.setAttribute('aria-hidden', 'true');
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-drawer-close]') || e.target.closest('.ss-drawer-overlay')) closeDrawer();
    var cartLink = e.target.closest('[data-cart-open]');
    if (cartLink && drawerEnabled) {
      e.preventDefault();
      renderDrawer().then(function () { openDrawer(false); });
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function updateCartCount(cart) {
    document.querySelectorAll('.ss-cart-count').forEach(function (el) { el.textContent = cart.item_count; });
  }

  function drawerLineHTML(item, line) {
    var img = item.image ? item.image.replace(/(\.[a-z]{3,4})(\?|$)/, '_160x$1$2') : '';
    var variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    return '<div class="ss-drawer-line" data-line="' + line + '" data-key="' + item.key + '">' +
      '<div class="ss-drawer-line__img">' + (img ? '<img src="' + img + '" alt="" width="72" height="72" loading="lazy">' : '') + '</div>' +
      '<div><strong>' + item.product_title + '</strong>' +
      (variantTitle ? '<small>' + variantTitle + '</small>' : '') +
      '<div class="ss-drawer__qty">' +
      '<button type="button" data-drawer-qty="-1" aria-label="Decrease quantity">&minus;</button>' +
      '<span>' + item.quantity + '</span>' +
      '<button type="button" data-drawer-qty="1" aria-label="Increase quantity">+</button>' +
      '</div></div>' +
      '<div class="ss-drawer-line__price">' + money(item.final_line_price) + '</div>' +
      '</div>';
  }

  function renderDrawer(lastAddedProductId) {
    if (!drawer) return Promise.resolve();
    return fetchCart().then(function (cart) {
      updateCartCount(cart);
      var bodyEl = drawer.querySelector('[data-drawer-lines]');
      var subtotal = drawer.querySelector('[data-drawer-subtotal]');
      if (subtotal) subtotal.textContent = money(cart.items_subtotal_price);
      var foot = drawer.querySelector('.ss-drawer__foot');
      if (foot) foot.style.display = cart.item_count === 0 ? 'none' : '';
      if (bodyEl) {
        if (cart.item_count === 0) {
          bodyEl.innerHTML = '<div class="ss-drawer__empty"><div class="ss-display">NOTHING IN THE TANK.</div><p>Your cart is empty. Time to shift.</p><a class="ss-btn ss-btn--primary ss-btn--sm" href="' + (drawer.getAttribute('data-all-url') || '/collections/all') + '">Shop Shapeshifter</a></div>';
        } else {
          bodyEl.innerHTML = cart.items.map(function (item, i) { return drawerLineHTML(item, i + 1); }).join('');
        }
      }
      renderCrossSells(cart, lastAddedProductId);
      return cart;
    });
  }

  function renderCrossSells(cart, productId) {
    var wrap = drawer && drawer.querySelector('[data-drawer-xsell]');
    if (!wrap) return;
    var seedId = productId || (cart.items[0] && cart.items[0].product_id);
    if (!seedId || cart.item_count === 0) { wrap.innerHTML = ''; return; }
    var inCart = cart.items.map(function (i) { return i.product_id; });
    fetch('/recommendations/products.json?product_id=' + seedId + '&limit=6&intent=related')
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) {
        var recs = (data.products || []).filter(function (p) { return inCart.indexOf(p.id) === -1 && p.available; }).slice(0, 3);
        if (!recs.length) { wrap.innerHTML = ''; return; }
        var heading = wrap.getAttribute('data-heading') || 'COMPLETE YOUR STACK';
        wrap.innerHTML = '<h4>' + heading + '</h4>' + recs.map(function (p) {
          var v = p.variants && p.variants[0];
          var img = p.featured_image ? p.featured_image.replace(/(\.[a-z]{3,4})(\?|$)/, '_120x$1$2') : '';
          return '<div class="ss-drawer__xsell-item">' +
            (img ? '<img src="' + img + '" alt="" width="56" height="56" loading="lazy">' : '<span></span>') +
            '<div><strong>' + p.title + '</strong><small>' + money(p.price) + '</small></div>' +
            (v ? '<button type="button" class="ss-btn ss-btn--ghost ss-btn--sm" data-quick-add="' + v.id + '">Add</button>' : '') +
            '</div>';
        }).join('');
      })
      .catch(function () { wrap.innerHTML = ''; });
  }

  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }

  /* Intercept product forms */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-ajax-atc]');
    if (!form || !drawerEnabled) return;
    e.preventDefault();
    var btn = form.querySelector('[data-atc]');
    var idInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');
    if (!idInput) return;
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    addToCart([{ id: parseInt(idInput.value, 10), quantity: qtyInput ? parseInt(qtyInput.value || '1', 10) : 1 }])
      .then(function (res) {
        var pid = res.items ? res.items[0].product_id : res.product_id;
        return renderDrawer(pid);
      })
      .then(function () { openDrawer(true); })
      .catch(function (err) {
        alert((err && err.description) || 'Could not add to cart.');
      })
      .then(function () {
        if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
      });
  });

  /* Quick add (cross-sells, cards) */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    addToCart([{ id: parseInt(btn.getAttribute('data-quick-add'), 10), quantity: 1 }])
      .then(function (res) {
        var pid = res.items ? res.items[0].product_id : res.product_id;
        return renderDrawer(pid);
      })
      .then(function () { openDrawer(true); })
      .catch(function () {})
      .then(function () { btn.disabled = false; });
  });

  /* Drawer qty +/- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-drawer-qty]');
    if (!btn) return;
    var line = btn.closest('.ss-drawer-line');
    var qtyEl = line.querySelector('.ss-drawer__qty span');
    var newQty = Math.max(0, parseInt(qtyEl.textContent, 10) + parseInt(btn.getAttribute('data-drawer-qty'), 10));
    line.style.opacity = '.5';
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.getAttribute('data-key'), quantity: newQty })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return renderDrawer(); })
      .catch(function () { line.style.opacity = ''; });
  });

  /* Cart page quantity forms fall back to native submit — no JS needed. */

  /* =========================================================
     THEME EDITOR SUPPORT — re-init after section re-render
     ========================================================= */
  document.addEventListener('shopify:section:load', function (e) {
    observeReveals(e.target);
    initHero(e.target);
    initInView(e.target);
    initAnnouncements();
    initShift();
    initParticles();
    initStickyAtc();
  });
})();
